import { DataPickerCst, InputCst, SelectCst } from "@components/InputCst";
import { Box, FormControlLabel, Grid, Switch, useTheme } from "@mui/material";
import {
  Col,
  General,
  Step1Props,
  ValuesInterface,
} from "@pages/Contents/Corporate/CollectCampaigns/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCollectCampaignState } from "../../context/CollectCampaignContext";
import { type Dayjs } from "dayjs";

const Step1: React.FC<Step1Props> = ({ row, columns, getValues, errors }) => {
  const theme = useTheme();
  const { t: collectCampaignTranslations } = useTranslation([
    "collect-campaign-page",
  ]);
  const { id, idCampaign } = useParams();
  const { state, dispatch } = useCollectCampaignState();

  const optionsUnits = ["HOURS", "POINTS", "CREDITS"].map((opt) => ({
    label: collectCampaignTranslations(opt),
    value: opt,
  }));

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          // minHeight: "495px",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Grid
          container
          spacing={3.75}
          sx={{
            justifyContent: "space-between",
          }}
        >
          {(columns as []).map((col: Col) => {
            const key = col.accessorKey as keyof General;
            if (col.type === "text") {
              return (
                <Grid key={key} item xs={12} sm={5}>
                  <InputCst
                    key={col.accessorKey}
                    id={col.accessorKey}
                    label={col.header}
                    value={state.general?.[key] as string | undefined}
                    required={col.required}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      dispatch({
                        type: "SET_GENERAL",
                        payload: {
                          ...state.general,
                          [key]: e.target.value,
                        } as General,
                      });
                    }}
                    maxValue={col?.rules?.max?.value}
                    multiline={col.multiline}
                    rows={col.rows}
                    disabled={col.disabled}
                    textTooltip={col.tooltip}
                    type={col.inpuType || "text"}
                    error={!!errors[col.accessorKey]}
                    errorMessage={`${collectCampaignTranslations(`validation.${errors[col.accessorKey]}`)}`}
                  />
                </Grid>
              );
            }
            if (col.type === "dateTime") {
              return (
                <Grid key={col.accessorKey} item xs={12} sm={3}>
                  <DataPickerCst
                    key={col.accessorKey}
                    id={col.accessorKey}
                    label={col.header}
                    value={state.general?.[key]}
                    required={col.required}
                    onChange={(value) => {
                      const newVal = value as Dayjs | undefined;
                      const dateVal = newVal
                        ? new Date(
                            Date.UTC(
                              newVal.year(),
                              newVal.month(),
                              newVal.date(),
                            ),
                          )
                        : undefined;
                      dispatch({
                        type: "SET_GENERAL",
                        payload: {
                          ...state.general,
                          [key]: dateVal,
                        } as General,
                      });
                    }}
                    error={!!errors[col.accessorKey]}
                    errorMessage={`${collectCampaignTranslations(`validation.${errors[col.accessorKey]}`)}`}
                    disabled={col.disabled}
                  />
                </Grid>
              );
            }
            return null;
          })}
          <Grid key={"is_learner_visible"} item xs={12} sm={5}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(state.general?.is_learner_visible)}
                  value={Boolean(state.general?.is_learner_visible)}
                  onChange={(event, checked) => {
                    dispatch({
                      type: "SET_GENERAL",
                      payload: {
                        ...state.general,
                        is_learner_visible: checked,
                      } as General,
                    });
                  }}
                />
              }
              label={`${collectCampaignTranslations("is_learner_visible")}`}
              labelPlacement="start"
              sx={{
                "&.MuiFormControlLabel-root": {
                  margin: 0,
                  justifyContent: "space-between",
                  width: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  pl: "1.8125rem",
                  pr: "0.65rem",
                  borderRadius: "2px",
                },
              }}
            />
          </Grid>
          <Grid key={"unit"} item xs={12} sm={3}>
            <SelectCst
              key={"unit"}
              id={"unit"}
              label={collectCampaignTranslations("unit") || ""}
              value={state.general?.["unit"]}
              required={true}
              onChange={(e) => {
                const selected = optionsUnits.find(
                  (item: ValuesInterface) => item.value === e,
                );
                dispatch({
                  type: "SET_GENERAL",
                  payload: {
                    ...state.general,
                    unit: selected?.value,
                  } as General,
                });
              }}
              options={optionsUnits}
              error={!!errors.unit}
              errorMessage={`${collectCampaignTranslations(`validation.${errors.unit}`)}`}
            />
          </Grid>
          <Grid key={"target"} item xs={12} sm={3}>
            <InputCst
              type="number"
              key={"target"}
              id={"target"}
              label={collectCampaignTranslations("target") ?? ""}
              value={`${state.general?.target ?? ""}`}
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                dispatch({
                  type: "SET_GENERAL",
                  payload: {
                    ...state.general,
                    target: Number(e.target.value),
                  } as General,
                });
              }}
              error={!!errors.target}
              errorMessage={`${collectCampaignTranslations(`validation.${errors.target}`)}`}
            ></InputCst>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Step1;
